import isEqual from 'lodash/isEqual.js'
import {
  useDataSubmit,
  useDataValue,
  useDataIsSubmitting,
  useDataOriginalValue,
} from 'Simple/Data'

export function useOnClick(props) {
  let submit = useDataSubmit({
    context: 'automation',
    viewPath: props.viewPath,
  })

  let event_id = useDataValue({
    context: 'automation',
    path: 'event_id',
    viewPath: props.viewPath,
  })

  return function onClick() {
    submit({ type: event_id ? 'update' : 'create' })
  }
}

function sanitizeAction(action) {
  if (!action || typeof action !== 'object') return action
  // drop the extra keys used in the UI for internal tracking
  let { lastGroupItem, displayOrder, level, singleGroupItem, ...rest } = action
  return rest
}

export function useIsDisabled(props) {
  let isSubmitting = useDataIsSubmitting({
    viewPath: props.viewPath,
    context: 'automation',
  })
  let automation = useDataValue({
    viewPath: props.viewPath,
    context: 'automation',
  })
  let original_automation = useDataOriginalValue({
    viewPath: props.viewPath,
    context: 'automation',
  })
  let normalized_automation = {
    ...automation,
    actions: automation.actions.map(sanitizeAction),
  }

  return (
    isSubmitting ||
    !isValidAutomation(automation) ||
    isEqual(normalized_automation, original_automation)
  )
}

function isValidAutomation({ actions = [], automation_type }) {
  return (
    Array.isArray(actions) &&
    actions.length > 0 &&
    actions.every(action => isValidAction(action, automation_type))
  )
}

function isValidAction({ type, data }, automation_type) {
  switch (type) {
    case 'task_insert': {
      if (automation_type === 'automatic') {
        return data.title && data.description && data.task_basket_id
      } else {
        return true
      }
    }
    case 'task_insert_new': {
      if (automation_type === 'automatic') {
        return (
          data.title &&
          data.board_id &&
          data.stage_id &&
          (data?.actions?.length > 0
            ? data.actions[0].document_template_id !== null
            : true)
        )
      }
      return true
    }
    case 'task_update': {
      return data.board_id
    }
    case 'status_update': {
      if (automation_type === 'automatic') {
        return data.new_status
      } else {
        let isValid = true

        if (data.is_subset) {
          isValid = !!data.subset_statuses.length
        }

        if (isValid && data.has_default_status) {
          isValid = !!data.default_status
        }

        return isValid
      }
    }
    case 'notification': {
      let isValid = true

      if (data.email_template.is_subset) {
        isValid = !!data.email_template.subset.length
      }

      if (isValid && data.email_template.has_default_value) {
        isValid = !!data.email_template.default_value
      }

      if (isValid && data.sms_template.is_subset) {
        isValid = !!data.sms_template.subset.length
      }

      if (isValid && data.sms_template.has_default_value) {
        isValid = !!data.sms_template.default_value
      }

      return isValid
    }
    case 'http': {
      // TODO: implement
      // if (!isValidUrl(data.request?.url) || !data.request?.method) {
      //   return false
      // }

      // This code is commented for now because in theory, the archive patient endpoint would have an empty body
      // if (['POST', 'PUT', 'PATCH'].includes(data.req?.method)) {
      //   return !!data.req?.body
      // }

      return true
    }
    case 'check_requirements':
      return (
        !!data?.requirements?.length &&
        !!data?.requirements?.every(isSkippableActionValid)
      )
    case 'send_communication': {
      if (!data.email && !data.sms) {
        return false
      }

      if (data.email) {
        if (!data.email_template.subject || !data.email_template.from) {
          return false
        }
        let hasContent = data.email_template.url
          ? 'content' in data.email_template
          : true
        let contentValid = (data.email_template?.content?.trim() || '') !== ''
        if (hasContent && !contentValid) {
          return false
        }
      }

      if (data.sms) {
        if (!data.sms_template.from) {
          return false
        }
        let hasContent = data.sms_template.url
          ? 'content' in data.sms_template
          : true
        let contentValid = (data.sms_template?.content?.trim() || '') !== ''
        if (hasContent && !contentValid) {
          return false
        }
      }

      return true
    }
    case 'wait': {
      if (!data.weeks && !data.days && !data.hours && !data.minutes) {
        return false
      }
      return true
    }
    case 'create_procedure_receivables': {
      return isSkippableActionValid(data)
    }
    default: {
      return false
    }
  }
}

function isSkippableActionValid(data) {
  // if a task is created when skipping requirements, then a task title should be provided and a target task basket selected
  let legacy_task_valid =
    !data.create_task || (data.task_title && data.task_basket_id)
  let new_task_valid =
    !data.create_task_new ||
    (data.new_task.title &&
      data.new_task.board_id &&
      data.new_task.stage_id &&
      (data.new_task.actions.length > 0
        ? data.new_task.actions[0].document_template_id !== null
        : true))
  return legacy_task_valid && new_task_valid
}
